import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FeatureFlagsContext, type FeatureFlags } from './FeatureFlagsContext';
import { type FlagName, FLAGS, FLAG_NAMES } from './registry';
import { type Overrides, loadOverrides, saveOverrides } from './overrides';
import { useAppContext } from '../core/context';
import { useDevice } from '../platform/device/useDevice';
import { calculateOpenInAppVariant } from '../core/util/openInAppVariant';
import { willTrack } from '../core/services/mixpanel';
import { setSentryContext, trackFeatureFlags } from '../core/instrumentation/sentry';

export const FeatureFlagsProvider = ({
  children,
  overrides: seedOverrides,
}: {
  children: ReactNode;
  overrides?: Overrides;
}) => {
  const { claims, hasAttemptedRedirect, mixpanel } = useAppContext();
  const device = useDevice();

  const [overrides, setOverrides] = useState<Overrides>(() => ({
    ...loadOverrides(),
    ...seedOverrides,
  }));

  const resolved = useMemo(
    () =>
      FLAG_NAMES.reduce<Record<FlagName, boolean>>(
        (acc, name) => {
          acc[name] = FLAGS[name].resolve({
            claims,
            device,
            hasAttemptedRedirect,
            mixpanelEnabled: willTrack(claims),
          });
          return acc;
        },
        {} as Record<FlagName, boolean>
      ),
    [claims, device, hasAttemptedRedirect]
  );

  const flags = useMemo(
    () =>
      FLAG_NAMES.reduce<Record<FlagName, boolean>>(
        (acc, name) => {
          acc[name] = overrides[name] ?? resolved[name];
          return acc;
        },
        {} as Record<FlagName, boolean>
      ),
    [resolved, overrides]
  );

  const setOverride = useCallback((name: FlagName, value: boolean | undefined) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === undefined) {
        delete next[name];
      } else {
        next[name] = value;
      }
      saveOverrides(next);
      return next;
    });
  }, []);

  const resetOverrides = useCallback(() => {
    setOverrides({});
    saveOverrides({});
  }, []);

  useEffect(() => {
    trackFeatureFlags(flags);
  }, [flags]);

  useEffect(() => {
    if (claims) {
      setSentryContext({
        brand: claims.brand,
        market: claims.market,
        apiSource: claims.apiSource,
        hasAttemptedRedirect,
        noEdit: claims.noEdit,
        blockPhoneNumberInput: claims.blockPhoneNumberInput,
      });
    }
  }, [claims, hasAttemptedRedirect]);

  useEffect(() => {
    if (claims) {
      mixpanel.setTrackingContext({
        brand: claims.brand,
        market: claims.market,
        paymentType: claims.paymentType,
        shouldShowFancyDeeplinkButton: flags.fancyDeeplink,
        openInAppVariant: calculateOpenInAppVariant(flags.fancyDeeplink),
        hasAttemptedRedirect,
        shouldTrackMixpanel: claims.shouldTrackMixpanel,
        showNewDesign: flags.redesign,
      });
    }
  }, [claims, flags.fancyDeeplink, flags.redesign, hasAttemptedRedirect, mixpanel]);

  const value: FeatureFlags = {
    flags,
    resolved,
    overrides,
    setOverride,
    resetOverrides,
  };

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};
